import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, type ButtonProps } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useProposalsStore } from '@/stores/proposals';
import { BotMessageSquare, Send } from 'lucide-react';
import { cn } from '@/lib/utils';

interface RespondToProposalProps extends React.HTMLAttributes<HTMLDivElement> {
  taskId: string;
  proposalId: string;
  size?: ButtonProps['size'];
  variant?: ButtonProps['variant'];
}

export const RespondToProposal = ({ taskId, proposalId, size = 'sm', variant = 'outline', className }: RespondToProposalProps) => {
  const [isResponding, setIsResponding] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { respondToProposal, byTaskId } = useProposalsStore();
  const navigate = useNavigate();

  useEffect(() => {
    if (isResponding) {
      textareaRef.current?.focus();
    }
  }, [isResponding]);

  const handleSubmit = async () => {
    if (!feedback.trim()) return;
    setIsSubmitting(true);
    const result = await respondToProposal(taskId, proposalId, feedback);
    setIsSubmitting(false);
    if (result) {
      const parentProposal = byTaskId[taskId]?.[proposalId];
      const providerId = parentProposal?.generatingProvider;
      let modelForNav = providerId;

      const builtInProviders = ['qwen', 'gemini', 'codex', 'claude', 'copilot'];
      if (providerId && !builtInProviders.includes(providerId)) {
        modelForNav = `llm_provider:${providerId}`;
      }

      if (!modelForNav) modelForNav = 'qwen'; // Fallback

      navigate(`/pages/generation-progress/?taskId=${result.taskId}&proposalId=${result.proposalId}&model=${modelForNav}`);
    }
  };

  if (isResponding) {
    return (
      <div className={cn("space-y-2 w-full", className)}>
        <Textarea
          ref={textareaRef}
          placeholder="Provide feedback to refine the proposal..."
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          disabled={isSubmitting}
          className="min-h-[80px]"
        />
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={() => setIsResponding(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSubmit} disabled={!feedback.trim() || isSubmitting}>
            <Send />
            {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <Button size={size} variant={variant} onClick={() => setIsResponding(true)} className={className}>
      <BotMessageSquare />
      Respond
    </Button>
  );
};